import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCreateRemoteParamMutation, useDeleteRemoteParamMutation, useUpdateRemoteParamMutation } from '../../api/remoteConfigurationApiSlice';
import Block from '../Block';
import Icon from '../Icon';
import styled from 'styled-components';
import useActionConfirmation from '../useActionConfirmation';


const StyledRowParams = styled(Row)`
  background-color: #dedede;
  padding-top: 7px;
  padding-bottom: 7px;
  border-radius: 7px;
  margin-bottom: 15px;
  margin-right: 0;
  margin-left: 0;
`

const StyledParamLabel = styled(Form.Label)`
  color: black;
  font-size: 0.8rem;
  padding-left: 0;
  line-height: 1.8;
`

const StyledCheckLabel = styled(Form.Label)`
  color: black;
  font-size: 0.8rem;
  padding-left: 0;
  margin-top: 2px;
  margin-bottom: 2px;
  line-height: 0.8;
`

const ParamButton = styled(Button)`
  color: ${props => props.color ? props.color : "#555555" };
  height: 100%;
  i {
    margin-right: 0px;
  }
`

const ParamButtonContainer = styled(Col)`
  white-space: nowrap;
  text-align: end;
`

const ParamsInput = ({paramKey, paramValue, paramCmp, onChange=f=>f, readOnly=false}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({key: paramKey, value: paramValue, cmp: paramCmp})
  useEffect(() => {
    onChange(formData["key"], formData["value"], formData["cmp"])
  }, [JSON.stringify(formData)])
  useEffect(() => {
    setFormData({key: paramKey, value: paramValue, cmp: paramCmp})
  }, [JSON.stringify([paramKey, paramValue, paramCmp])])

  return (
    <>
      <StyledParamLabel column className="text-right">
        {t("remoteConfiguration.key")}
      </StyledParamLabel>
      <Col md="3">
        <Form.Control
          size="sm"
          type="text"
          value={formData.key}
          readOnly={readOnly}
          onChange={(e) => { setFormData({ ...formData, key: e.target.value }) }}
        />
      </Col>
      <StyledParamLabel column className="text-right">
        {t("remoteConfiguration.value")}
      </StyledParamLabel>
      <Col md="3">
        <Form.Control
          size="sm"
          type="number"
          value={formData.value}
          readOnly={readOnly}
          onChange={(e) => { setFormData({ ...formData, value: e.target.value }) }}
        />
      </Col>
      <Col md="2" className="text-center d-flex flex-column align-items-center justify-content-center">
        <StyledCheckLabel>
          CMP
        </StyledCheckLabel>
        <Form.Check
          type="checkbox"
          disabled={readOnly}
          checked={formData.cmp}
          onChange={(e) => { setFormData({ ...formData, cmp: e.target.checked}) }}
        />
      </Col>
    </>
  )
}

const RemoteParamForm = ({onValidate, onCancel, initialValue={key: "", value: "", cmp: false}}) => {
  const [formData, setFormData] = useState(initialValue)
  const [showCommentModal, setShowCommentModal] = useState(false)
  const { t } = useTranslation()
  const onClose = ()=>(setShowCommentModal(false))

  return (
    <>
      <StyledRowParams className='justify-content-md-center'>
        <ParamsInput
          paramKey={formData.key}
          paramValue={formData.value}
          paramCmp={formData.cmp}
          readOnly={false}
          onChange={(key, value, cmp) => { setFormData( { ...formData, key: key, value: value, cmp: cmp } ) } }
        />
        <ParamButtonContainer md="2">
          <ParamButton variant="light" onClick={onCancel} >
            <Icon icon={'close-circle'}/>
          </ParamButton>
          {'  '}
          <ParamButton variant="light" color='#ff513c' onClick={()=>setShowCommentModal(true)} >
            <Icon icon={'checkCircle'}/>
          </ParamButton>
        </ParamButtonContainer>
      </StyledRowParams>
      <Modal
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        show={showCommentModal}
        onHide={onClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
          {t("comment")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={3}
            value={formData.comment}
            onChange={ (evt)=>{ setFormData( { ...formData, comment: evt.target.value } ) } }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button key="submit" type="submit" variant="primary" onClick={() => (onValidate(formData))}>
            <Icon icon='save' text={t("save")}/>
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

const NewRemoteParameter = ({configuration}) => {
  const [showNew, setShowNew] = useState(false)
  const addParam = ()=>(setShowNew(true))
  const [createRemoteParam] = useCreateRemoteParamMutation()
  const createRemoteParamConfig = (configuration) => (formData) => {
    createRemoteParam({configuration, remoteParam: formData}).then((response)=>{
      setShowNew(false)
    })
  }
  const onCancel=()=>{setShowNew(false)}
  return (
    <>
      { showNew && <RemoteParamForm onCancel={onCancel} onValidate={createRemoteParamConfig(configuration)}/> }
      <Row>
        <Col md={12} className="text-center">
          <Button variant='primary' onClick={addParam}>
            +
          </Button>
        </Col>
      </Row>
    </>
  )
}

const EditableRemoteParameterShow = ({parameter, onEdit, onDelete}) => {
  return (
    <>
      <StyledRowParams>
        <ParamsInput
          paramKey={parameter.key}
          paramValue={parameter.value}
          paramCmp={parameter.cmp}
          readOnly={true}
        />
        <ParamButtonContainer md="2">
          <ParamButton
            variant="light"
            onClick={onDelete}
          >
            <Icon icon={'trash'}/>
          </ParamButton>
          {'  '}
          <ParamButton
            variant="light"
            onClick={onEdit}
            color='#ff513c'
          >
            <Icon icon={'edit'}/>
          </ParamButton>
         </ParamButtonContainer>
      </StyledRowParams>
    </>
  )
}

const EditableRemoteParameter = ({configuration, parameter}) => {
  const [isEditable, setIsEditable] = useState(false)
  const [deleteRemoteParam] = useDeleteRemoteParamMutation()
  const {Confirmation, confirmationProps, confirm} = useActionConfirmation()
  const onDelete = ()=>{
    confirm(()=>{
      deleteRemoteParam({configuration, remoteParam: parameter })
    })
  }
  const onEdit = ()=>(setIsEditable(true))
  const onCancel = ()=>(setIsEditable(false))
  const [updateRemoteParam] = useUpdateRemoteParamMutation()
  const onValidate = (formData)=>{
    updateRemoteParam({configuration, remoteParam: { ...parameter , ...formData}}).then(
      (response) => { setIsEditable(false) }
    )
  }

  return (
    <>
      <Confirmation placeholder="Please enter value to confirm" {...confirmationProps} deletion />
      { isEditable &&
        <RemoteParamForm
          initialValue={parameter}
          onCancel={onCancel}
          onValidate={onValidate}/> }
      { !isEditable && <EditableRemoteParameterShow
          parameter={parameter}
          onEdit={onEdit}
          onDelete={onDelete} /> }
    </>
  )
}

const InheritedRemoteParameterShow = ({parameter, onClone}) => {
  return (
    <>
      <StyledRowParams>
        <ParamsInput
          paramKey={parameter.key}
          paramValue={parameter.value}
          paramCmp={parameter.cmp}
          readOnly={true}
        />
        <ParamButtonContainer md="2">
          <ParamButton
            variant="light"
            color='#ff513c'
            onClick={onClone}
          >
            <Icon icon={'clone'}/>
          </ParamButton>
        </ParamButtonContainer>
      </StyledRowParams>
    </>
  )
}

const InheritedRemoteParameter = ({configuration, parameter}) => {
  const [isEditable, setIsEditable] = useState(false)
  const onCancel = ()=>(setIsEditable(false))
  const onClone = ()=>(setIsEditable(true))
  const [createRemoteParam] = useCreateRemoteParamMutation()
  const onValidate = (formData)=>{
    createRemoteParam({configuration, remoteParam: formData}).then(
      (response) => { setIsEditable(false) }
    )
  }

  if (isEditable) {
    return ( <RemoteParamForm
                initialValue={parameter}
                onCancel={onCancel}
                onValidate={onValidate}/> )
  } else {
    return ( <InheritedRemoteParameterShow
                parameter={parameter}
                onClone={onClone} /> )
  }
}

const RemoteParameterShow = ({parameter, configuration}) => {
  if (parameter.inherited) {
    return (<>
      <InheritedRemoteParameter parameter={parameter} configuration={configuration}/>
    </>)
  } else {
    return (<>
      <EditableRemoteParameter parameter={parameter} configuration={configuration}/>
    </>)
  }
}

const RemoteParametersList = ({configuration}) => {
  const { t } = useTranslation()

  return (<>
    { [...configuration["aggregated_params_object"]].sort((a, b)=>(a['key'].localeCompare(b['key']))).map((parameter)=>(
      <RemoteParameterShow key={parameter.id} configuration={configuration} parameter={parameter}/>
    ))}
  </>)
}

const RemoteConfigurationShow = ({configuration}) => {
  const { t } = useTranslation()

  return (
    <Block style={{"maxWidth": "700px"}} >
      <RemoteParametersList configuration={configuration} />
      <NewRemoteParameter configuration={configuration}/>
    </Block>
  )
}

export { RemoteParametersList, NewRemoteParameter }
export default RemoteConfigurationShow